import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import type { AssignmentHistoryEntry } from '@cortex/domain-project-intake-contract';
import { TEAM_OPTIONS } from '@cortex/domain-product-innovation-lab-kpi-team-filter';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import { SortableHeader } from '../KpiDetailModal/SortableHeader.js';
import { formatYmdSlash } from '../../../lib/date-utils.js';
import type { SortDir } from '../../../lib/table-sort.js';
import {
  EMPTY_ASSIGNMENT_HISTORY_FILTERS,
  filterAndSortAssignmentHistory,
  type AssignmentHistoryFilters,
  type AssignmentHistorySortKey,
  type AssignmentHistoryTextFilterKey,
} from '../../../lib/assignment-history-view.js';
import { useUpdateAssignmentIntent } from '../../../hooks/useAssignmentHistory.js';

/** 見出しのセル。 @graph-connects none */
const TH =
  'text-left py-3 px-3 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap';
/** 本文のセル。 @graph-connects none */
const TD = 'py-3 px-3 text-sm text-[var(--color-text-secondary)] align-top';
/** フィルター行の入力。 @graph-connects none */
const FILTER_INPUT =
  'w-full bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-md px-2 py-1 text-xs text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[var(--color-border-focus)]';

/** 列の並び（見出し・フィルター・本文で共有する）。 @graph-connects none */
const MEMBER_COLUMNS = ['requirementEngineer', 'developer', 'reviewer'] as const;

/**
 * アサイン意図の入力と保存ボタン。保存すると API が返した値で一覧が置き換わり、
 * 親が `key`（履歴 ID + 保存時刻）を変えるので下書きは保存後の値で作り直される。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business アサイン履歴 1 件のアサイン意図を編集し、保存ボタンで履歴データへ保存する
 * @graph-connects useUpdateAssignmentIntent [calls] アサイン意図の保存
 */
function IntentEditor({ entry }: { entry: AssignmentHistoryEntry }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(entry.intent);
  const mutation = useUpdateAssignmentIntent();
  const unchanged = draft.trim() === entry.intent;

  return (
    <div className="flex flex-col gap-1.5">
      <Textarea
        aria-label={t('assignmentHistory.columns.intent')}
        placeholder={t('assignmentHistory.intentPlaceholder')}
        rows={2}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
        }}
        className="px-2 py-1 text-xs"
      />
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={unchanged || mutation.isPending}
          onClick={() => {
            mutation.mutate({ id: entry.id, intent: draft });
          }}
        >
          {mutation.isPending ? t('assignmentHistory.saving') : t('assignmentHistory.save')}
        </Button>
        {mutation.isError && (
          <span role="alert" className="text-xs text-[var(--color-error)]">
            {mutation.error instanceof Error
              ? mutation.error.message
              : t('assignmentHistory.saveFailed')}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * 案件アサイン履歴テーブル。案件番号・案件名・アサイン日・役割ごとのメンバーの各列で
 * 並び替えと絞り込みができ、各行でアサイン意図を保存できる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件アサイン履歴を表で表示し、案件番号・案件名・アサイン日・要件定義開発者・開発者・レビュワーで並び替え・絞り込み（メンバーは KPI のチーム単位でも絞れる）し、各行のアサイン意図を保存する
 * @graph-connects filterAndSortAssignmentHistory [calls] 絞り込みと並び替え
 * @graph-connects IntentEditor [calls] アサイン意図の編集と保存
 */
export function AssignmentHistoryTable({
  items,
  memberTeams,
}: {
  items: AssignmentHistoryEntry[];
  /** メンバー（小文字の nickname）→ 今の所属で当たる KPI チーム（API が返したまま）。 */
  memberTeams: Readonly<Record<string, readonly string[]>>;
}) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AssignmentHistoryFilters>(
    EMPTY_ASSIGNMENT_HISTORY_FILTERS,
  );
  const [sort, setSort] = useState<{ key: AssignmentHistorySortKey; dir: SortDir }>({
    key: 'assignedDate',
    dir: 'desc',
  });
  const rows = useMemo(
    () => filterAndSortAssignmentHistory(items, filters, sort, memberTeams),
    [items, filters, sort, memberTeams],
  );

  const onSort = (key: AssignmentHistorySortKey) => {
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'assignedDate' ? 'desc' : 'asc' },
    );
  };
  const setFilter = (key: keyof AssignmentHistoryFilters, value: string) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const header = (key: AssignmentHistorySortKey) => (
    <th
      key={key}
      className={TH}
      aria-sort={sort.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <SortableHeader
        label={t(`assignmentHistory.columns.${key}`)}
        sortKey={key}
        currentKey={sort.key}
        currentDir={sort.dir}
        onSort={onSort}
      />
    </th>
  );
  const textFilter = (key: AssignmentHistoryTextFilterKey) => (
    <th key={key} className="px-3 pb-2">
      <input
        type="search"
        aria-label={`${t(`assignmentHistory.columns.${key}`)} ${t('assignmentHistory.filter.placeholder')}`}
        placeholder={t('assignmentHistory.filter.placeholder')}
        value={filters[key]}
        onChange={(e) => {
          setFilter(key, e.target.value);
        }}
        className={FILTER_INPUT}
      />
    </th>
  );

  if (items.length === 0) {
    return (
      <p className="text-center py-8 text-[var(--color-text-muted)]">
        {t('assignmentHistory.empty')}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      {/* 案件名だけ可変幅にして、サイドバーを除いた 1,100px 前後に収める（意図の欄を切らない）。 */}
      <table className="w-full min-w-[1050px] table-fixed">
        <colgroup>
          <col className="w-[140px]" />
          <col />
          <col className="w-[120px]" />
          <col className="w-[120px]" />
          <col className="w-[90px]" />
          <col className="w-[90px]" />
          <col className="w-[100px]" />
          <col className="w-[210px]" />
        </colgroup>
        <thead>
          <tr>
            {header('issueKey')}
            {header('summary')}
            {header('assignedDate')}
            {MEMBER_COLUMNS.map((key) => header(key))}
            <th className={TH}>{t('assignmentHistory.columns.assignedBy')}</th>
            <th className={TH}>{t('assignmentHistory.columns.intent')}</th>
          </tr>
          <tr>
            {textFilter('issueKey')}
            {textFilter('summary')}
            <th className="px-3 pb-1">
              <input
                type="date"
                aria-label={t('assignmentHistory.filter.from')}
                value={filters.assignedFrom}
                onChange={(e) => {
                  setFilter('assignedFrom', e.target.value);
                }}
                className={FILTER_INPUT}
              />
            </th>
            {MEMBER_COLUMNS.map((key) => textFilter(key))}
            <th />
            <th />
          </tr>
          {/* 2 段目: 終了日と、3 つの役割をまとめて判定するチーム（KPI と同じ単位） */}
          <tr className="border-b border-[var(--color-border)]">
            <th />
            <th />
            <th className="px-3 pb-2">
              <input
                type="date"
                aria-label={t('assignmentHistory.filter.to')}
                value={filters.assignedTo}
                onChange={(e) => {
                  setFilter('assignedTo', e.target.value);
                }}
                className={FILTER_INPUT}
              />
            </th>
            <th colSpan={MEMBER_COLUMNS.length} className="px-3 pb-2">
              <select
                aria-label={t('assignmentHistory.filter.team')}
                value={filters.team}
                onChange={(e) => {
                  setFilter('team', e.target.value);
                }}
                className={FILTER_INPUT}
              >
                {TEAM_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.value === 'all' ? t('assignmentHistory.filter.allTeams') : option.label}
                  </option>
                ))}
              </select>
            </th>
            <th />
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={8} className="text-center py-8 text-[var(--color-text-muted)]">
                {t('assignmentHistory.noMatch')}
              </td>
            </tr>
          ) : (
            rows.map((entry) => (
              <tr
                key={entry.id}
                className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)] transition-colors"
              >
                <td className={`${TD} whitespace-nowrap`}>
                  <Link
                    to="/projects/$pjKey"
                    params={{ pjKey: entry.issueKey }}
                    className="text-[var(--color-accent-primary)] hover:underline"
                  >
                    {entry.issueKey}
                  </Link>
                </td>
                <td
                  className={`${TD} text-[var(--color-text-primary)] break-words`}
                  title={entry.summary ?? undefined}
                >
                  {entry.summary ?? '-'}
                </td>
                <td className={`${TD} whitespace-nowrap`}>{formatYmdSlash(entry.assignedDate)}</td>
                {MEMBER_COLUMNS.map((key) => (
                  <td key={key} className={`${TD} truncate`}>
                    {entry[key] ?? '-'}
                  </td>
                ))}
                <td className={`${TD} truncate`} title={entry.assignedBy}>
                  {entry.assignedBy || '-'}
                </td>
                <td className={TD}>
                  <IntentEditor key={`${entry.id}:${entry.intentUpdatedAt ?? ''}`} entry={entry} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
